import * as React from 'react';
// eslint-disable-next-line aveny/mui-import-rule
import NoSsr from '@mui/material/NoSsr';
import { useAxesTooltip, useMouseTracker } from '@mui/x-charts-pro';
import EDTypography from '../../../../../../../../../../BaseComponents/Typography/EDTypography';
import EDPopper from '../../../../../../../../../../BaseComponents/Popper/EDPopper';
import EDBox from '../../../../../../../../../../BaseComponents/EDBox/EDBox';
import { generateVirtualElement } from '../../../../../Utils/detaljanalysUtils';

interface IProps {
  currencyValues?: boolean;
  currentMonth?: number;
}

export const EkonomiskAnalysCustomAxisTooltip: React.FC<IProps> = props => {
  const axisData = useAxesTooltip();
  const mousePosition = useMouseTracker(); // Track the mouse position on chart.

  if (!axisData || !mousePosition) {
    // No data to display
    return null;
  }

  // The pointer type can be used to have different behavior based on pointer type.
  const isMousePointer = mousePosition?.pointerType === 'mouse';
  // Adapt the tooltip offset to the size of the pointer.
  const yOffset = isMousePointer ? 0 : 40 - mousePosition.height;

  return (
    <NoSsr>
      <EDPopper
        sx={{
          pointerEvents: 'none',
          zIndex: theme => theme.zIndex.modal,
        }}
        open
        placement={isMousePointer ? 'bottom-start' : 'bottom-start'}
        anchorEl={generateVirtualElement(mousePosition)}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, yOffset],
            },
          },
        ]}
      >
        {axisData.map((tooltipData, index) => (
          <EDBox
            key={index}
            sx={{
              m: 1,
              backgroundColor: 'background.paper',
              border: 'solid',
              borderWidth: 2,
              borderColor: 'divider',
              table: { borderSpacing: 0 },
              thead: {
                td: {
                  px: 1.5,
                  py: 0.75,
                  borderBottom: 'solid',
                  borderWidth: 2,
                  borderColor: 'divider',
                },
              },
              tbody: {
                'tr:first-child': { td: { paddingTop: 1.5 } },
                'tr:last-child': { td: { paddingBottom: 1.5 } },
                tr: {
                  'td:first-child': { paddingLeft: 1.5 },
                  'td:last-child': { paddingRight: 1.5 },
                  td: {
                    paddingRight: '7px',
                    paddingBottom: '10px',
                  },
                },
              },
            }}
          >
            <table>
              <thead>
                <tr>
                  <td colSpan={3}>
                    <EDTypography>
                      {tooltipData.axisFormattedValue}
                    </EDTypography>
                  </td>
                </tr>
              </thead>
              <tbody>
                {tooltipData.seriesItems
                  .filter(item => item.formattedValue !== '')
                  .map(seriesItem => {
                    if (
                      seriesItem.seriesId === 'projectedUtgaendeVarde' &&
                      props.currentMonth !== undefined &&
                      tooltipData.axisValue === props.currentMonth
                    ) {
                      //Edge case när projected värde för nuvarande månad inte ska visas
                      return null;
                    }

                    return (
                      <tr key={seriesItem.seriesId}>
                        <td>
                          <div
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              backgroundColor: seriesItem.color,
                            }}
                          />
                        </td>
                        <td>
                          <EDTypography fontWeight="light">
                            {seriesItem.formattedLabel}
                          </EDTypography>
                        </td>
                        <td>
                          <EDTypography
                            sx={{
                              float: props.currencyValues ? 'right' : 'none',
                            }}
                          >
                            {seriesItem.formattedValue}
                          </EDTypography>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </EDBox>
        ))}
      </EDPopper>
    </NoSsr>
  );
};
